import { localizeURL } from '@tesla/parse-locale';

// Types
import {
  SET_LOCATIONS,
  SET_LOCATIONS_CACHE,
  SET_LOCATION_FILTERS,
  SET_FILTERS_VISIBILITY,
  SET_SELECTED_LOCATION,
  SET_INITIAL_LOCATION,
  SET_SELECTED_LOADING,
  SET_LOCATION_DETAILS_CACHE,
} from '../constants/types';

import { API_BASE_URL, FILTERS, MAX_BOUNDS } from '../constants';

// Helpers
import * as requests from '../lib/requests';
import { setUrl } from '../actions/app';
import { toggleItemInArray } from '../lib/Utils';
import { updateUrlFilters, updateUrlLocation } from '../lib/UrlUtils';
import { setGeolocation } from './map';

import {
  formatLocationData,
  retrieveFromCache,
  getUpdatedLocationsCache,
} from '../lib/LocationUtils';

import {
  formatLocationDetailsData,
  getUpdatedLocationDetailsCache,
} from '../lib/LocationDetailsUtils';

// Static actions
export const setLocationFilters = filters => (dispatch, getState) => {
  dispatch(setUrl(updateUrlFilters(filters, getState().app.url)));

  dispatch({
    type: SET_LOCATION_FILTERS,
    filters,
  });
};

export const setFiltersVisibility = visible => {
  return {
    type: SET_FILTERS_VISIBILITY,
    visible,
  };
};

export const setLocations = locations => {
  return {
    type: SET_LOCATIONS,
    locations,
  };
};

export const setLocationsCache = cache => {
  return {
    type: SET_LOCATIONS_CACHE,
    cache,
  };
};

export const setSelectedLoading = loading => {
  return {
    type: SET_SELECTED_LOADING,
    loading,
  };
};

export const setSelectedLocation = location => (dispatch, getState) => {
  dispatch(setUrl(updateUrlLocation(location ? location.id : null, getState().app.url)));
  dispatch({
    type: SET_SELECTED_LOCATION,
    location,
  });
};

/**
 * Used to identify if the map should zoom into a specific location upon load.
 *
 * @param {String} - Location id.
 * @return {Object} - Action object.
 */
export const setInitialLocation = location => {
  return {
    type: SET_INITIAL_LOCATION,
    location,
  };
};

export const setLocationDetailsCache = cache => {
  return {
    type: SET_LOCATION_DETAILS_CACHE,
    cache,
  };
};

/**
 * Retrieves locations for the provided bounds and filters, either from local cache or via
 * external request.
 *
 * @param {Object} bounds - Bounds object containing the northeast and southwest lat and lng.
 * @param {Array<String>} filters - Array of filters selected.
 * @return {Array<Object>} - Array of formatted locations.
 */
export const retrieveLocations = (bounds, filters = []) => async (dispatch, getState) => {
  const state = getState().app;
  const path = state.api.locations;
  const { locale } = state;

  // Locations for the provided bounds and filters.
  let locations = [];

  // If no filters are selected, then no locations should be shown.
  if (!filters || filters.length === 0) return dispatch(setLocations(locations));

  // Otherwise check if locations are available from cache. If only a partial amount are available
  // in cache, make requests to retrieve remaining locations.
  let cache = getState().locations.locationsCache;

  const { locations: cachedLocations, remaining } = retrieveFromCache(bounds, filters, cache);
  locations = locations.concat(cachedLocations);

  // Only make requests if progressive location querying is active.
  const progressiveQuerying = state.performance && state.performance.progressiveLocations;
  if (!progressiveQuerying) return dispatch(setLocations(locations));

  // For the remaining bounds / filters combos that are not in cache, make a request to retrieve
  // those data points.
  await Promise.all(
    remaining.map(async req => {
      const { bounds: reqBounds, filters: reqFilters } = req;

      const params = {
        // eslint-disable-next-line prettier/prettier
        bounds: `${reqBounds.northeast.lat},${reqBounds.northeast.lng},${reqBounds.southwest.lat},${
          reqBounds.southwest.lng
        }`,
        type: reqFilters.join(',').replace(' ', '_'),
        map_only: true,
      };

      const url = localizeURL(path, {
        locale: 'en_US',
        base_url: API_BASE_URL || null,
        query: {
          translate: locale,
        },
      });

      let response;

      try {
        response = await requests.get(url, params);
      } catch (e) {
        // Error with the request.
      }

      if (response) {
        const formattedData = formatLocationData(response);
        locations = locations.concat(formattedData);

        // Retrieve latest cache and generate an updated cache with the new data points.
        cache = getState().locations.locationsCache;
        const updatedCache = getUpdatedLocationsCache(reqBounds, reqFilters, formattedData, cache);
        dispatch(setLocationsCache(updatedCache));
      }
    })
  );

  return dispatch(setLocations(locations));
};

/**
 * Retrieve all locations. This is a cached response so even though it is larger for the client to
 * retrieve, it was more efficient from the server standpoint and might even have better response
 * times.
 */
export const retrieveAllLocations = () => async (dispatch, getState) => {
  const state = getState().app;
  const { locale, country } = state;
  const path = state.api.locations;
  const params = country === 'CN' ? { map: 'baidu' } : {};

  let locations = [];

  await new Promise(async resolve => {
    let response;

    try {
      const url = localizeURL(path, {
        locale: 'en_US',
        base_url: API_BASE_URL || null,
        query: {
          translate: locale,
        },
      });
      response = await requests.get(url, params);
    } catch (e) {
      // Error with the request.
    }

    if (response) {
      const formattedData = formatLocationData(response);
      locations = locations.concat(formattedData);

      // Update cache with all locations. Set the bounds to encompass the max / min lat lng possible
      // for the google map so that all future requests are queried from cache.
      const cache = getState().locations.locationsCache;
      const updatedCache = getUpdatedLocationsCache(MAX_BOUNDS, FILTERS, formattedData, cache);

      dispatch(setLocationsCache(updatedCache));
    }

    resolve();
  });

  // Retrieve locations for the initial state.
  const { bounds } = getState().map;
  const { filters } = getState().locations;

  // Update the locations for the current bounds and filters.
  dispatch(retrieveLocations(bounds, filters));
};

/**
 * Retrieves location info for the provided location id.
 *
 * @param {String} id - Id of the location to receive data for.
 */
export const retrieveLocationDetails = id => async (dispatch, getState) => {
  if (!id) return;

  const { locale } = getState().app;
  const path = getState().app.api.locationDetails;
  const cache = getState().locations.locationDetailsCache;
  const params = {
    id,
  };

  // Don't geolocate the user if they have searched.
  dispatch(setGeolocation(true));

  // Check if this location is cached.
  const cachedValue = cache[id];
  if (cachedValue) {
    dispatch(setSelectedLocation(cachedValue));
    return;
  }

  // If not cached, request the data.
  let response = null;
  dispatch(setSelectedLoading(true));

  try {
    const url = localizeURL(path, {
      locale: 'en_US',
      base_url: API_BASE_URL || null,
      query: {
        translate: locale,
      },
    });
    response = await requests.get(url, params);
  } catch (e) {
    // Error with the request.
  }

  if (response && typeof response === 'object') {
    const location = formatLocationDetailsData(response);

    if (location) {
      dispatch(setSelectedLocation(location));
      dispatch(setLocationDetailsCache(getUpdatedLocationDetailsCache(location, cache)));
    }
  }

  dispatch(setSelectedLoading(false));
};

export const toggleLocationFilter = filter => (dispatch, getState) => {
  const { filters } = getState().locations;
  const { bounds } = getState().map;
  const updatedFilters = toggleItemInArray(filters, filter);

  dispatch(setLocationFilters(updatedFilters));
  dispatch(retrieveLocations(bounds, updatedFilters));
};

export const updateLocationFilters = filters => (dispatch, getState) => {
  const { bounds } = getState().map;

  dispatch(setLocationFilters(filters));
  dispatch(retrieveLocations(bounds, filters));
};
